import { PageController } from '@client/components/_core/layout/PageController';
import { AiLodgementPage } from '@client/components/lodgement/ai-lodgement/AiLodgementPage';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { FileStack } from 'lucide-react';
import { z } from 'zod';

/**
 * The largest id the server's `bigint` columns hold, as digits: a larger value in the URL opens a
 * fresh wizard. Compared as text because zod still runs the refinement when the digits pattern
 * failed, and `BigInt('abc')` would throw where `.catch` only covers issues.
 */
const INT8_MAX_DIGITS = '9223372036854775807';

const searchSchema = z.object({
    runId: z
        .string()
        .regex(/^[1-9]\d{0,18}$/)
        .refine((value) => value.length < INT8_MAX_DIGITS.length || value <= INT8_MAX_DIGITS)
        .optional()
        .catch(undefined),
});

/**
 * Lodge a claim from documents (CUR-1744): staff drop a claim email or files, the server unpacks and
 * reads them, and AI prepares the staff lodgement draft. Opened from the claims list New claim menu.
 * The server gates every call on the products-v2 flag as well.
 */
export const Route = createFileRoute('/console/$orgId/claims/lodge-from-documents')({
    staticData: {
        metadata: { title: 'Lodge from documents' },
        breadcrumb: { title: 'Lodge from documents' },
        ui: {
            icon: FileStack,
            title: 'Lodge from documents',
            subtitle: 'Upload a claim email or documents. AI reads them and suggests the product for you to confirm.',
        },
        modules: ['claim'],
        permissions: ['claim:create'],
        // Literal (AI_CLAIM_LODGEMENT_FLAG_KEY): the route registry generator reads it statically.
        featureFlag: 'enable-ai-claim-lodgement',
    },
    validateSearch: searchSchema,
    component: RouteComponent,
});

function RouteComponent() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { runId } = Route.useSearch();
    return (
        <PageController route={Route}>
            <AiLodgementPage
                runId={runId ? BigInt(runId) : undefined}
                onRunIdChange={(next) =>
                    navigate({
                        to: '/console/$orgId/claims/lodge-from-documents',
                        params: { orgId },
                        search: next === undefined ? {} : { runId: next.toString() },
                        replace: true,
                    })
                }
                onLeave={() => navigate({ to: '/console/$orgId/claims', params: { orgId } })}
                onOpenClaim={(claimId) =>
                    navigate({ to: '/console/$orgId/claims/details/$id', params: { orgId, id: claimId } })
                }
            />
        </PageController>
    );
}
